import { graphql } from "gatsby";
import Sidebar, { Datafile, vocab } from "../components/Sidebar";
import React, {
  DOMElement,
  ForwardedRef,
  useEffect,
  useRef,
  useState,
} from "react";
import Layout from "../components/Layout";
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image";
import { css } from "twin.macro";
import { PrimaryButton } from "../components/Buttons";
import _ from "lodash";

import HeaderImg from "../img/word-scramble-bubble.png";
import AskLogo from "../img/logo.png";

const WordForm = React.forwardRef(
  (
    {
      word,
      resultUtils,
      scoreUtils,
    }: {
      word: String;
      resultUtils: [string, React.Dispatch<React.SetStateAction<string>>];
      scoreUtils: [number, React.Dispatch<React.SetStateAction<number>>];
    },
    ref: ForwardedRef<any>
  ) => {
    const [currLetter, setCurrLetter] = useState(-1);
    const [currWord, setCurrWord] = useState(_.fill(Array(word.length), ""));
    const wordWithoutSpaces = word.replace(/\s+/g, "");
    const [result, setResult] = resultUtils;
    const [score, setScore] = scoreUtils; // this should be a state machine
    const [lastBackspace, setLastBackspace] = useState(false);
    let _t = 0;
    return (
      <form
        tw="flex flex-wrap items-center justify-center text-sm lg:text-base xl:text-xl"
        onSubmit={(event) => event.preventDefault()}
      >
        {word.split(" ").map((w, _j) => {
          _t += w.length + 1;
          return (
            <section tw="mx-2 mt-2">
              {w.split("").map((e: String, _i) => {
                _i = _i + (_t - (w.length + 1));
                return (
                  <input
                    //   className={`word-${"sushi-fuku"}-${_i} ${
                    //     currLetter === _i && "meow"
                    //   }`}
                    key={_i}
                    data-key={_i}
                    size={1}
                    maxLength={1}
                    tw="border-b-2 border-black bg-transparent focus:bg-white focus:bg-opacity-25 outline-none mx-1 text-center! rounded-none shadow-none"
                    type="text"
                    ref={_i === currLetter ? ref : null}
                    onFocus={() => setCurrLetter(_i)}
                    onKeyUp={(_event: React.KeyboardEvent) => {
                      const target = _event.target as HTMLInputElement;

                      if (_event.key === "ArrowLeft") {
                        setCurrLetter(
                          _.clamp(
                            word.charAt(_i - 1) === " "
                              ? currLetter - 2
                              : currLetter - 1,
                            0,
                            word.length - 1
                          )
                        );
                        return;
                      }

                      if (_event.key === "ArrowRight") {
                        setCurrLetter(
                          _.clamp(
                            word.charAt(_i + 1) === " "
                              ? currLetter + 2
                              : currLetter + 1,
                            0,
                            word.length - 1
                          )
                        );
                        return;
                      }

                      // handle backspace
                      if (
                        _event.key === "Backspace" ||
                        _event.key === "Delete"
                      ) {
                        if (result === "success") {
                          setScore(score - 1);
                        }
                        setResult("progress");
                        const newWord = [...currWord];
                        newWord[_i] = target.value;
                        setCurrWord(newWord);
                        setCurrLetter(
                          _i === word.length - 1 && !lastBackspace
                            ? (setLastBackspace(true), word.length - 1)
                            : _i - 1 > 0
                            ? word.charAt(_i - 1) === " "
                              ? _i - 2
                              : _i - 1
                            : 0
                        );
                      }

                      // current letter has a letter
                      if (target.value?.length >= target.maxLength) {
                        // reset backspace behaviour hack
                        setLastBackspace(false);

                        // update cursor position
                        setCurrLetter(
                          word.charAt(_i + 1) === " " ? _i + 2 : _i + 1
                        );

                        // update word state
                        const newWord = [...currWord];
                        newWord[_i] = target.value;
                        setCurrWord(newWord);

                        if (
                          newWord.join("").length === wordWithoutSpaces.length
                        ) {
                          if (
                            newWord.join("").toLowerCase() ===
                            wordWithoutSpaces.toLowerCase()
                          ) {
                            // succeed
                            if (result === "progress") {
                              // alert("success");
                              setScore(score + 1);
                            } else if (result === "failure") {
                              setScore(score + 2);
                            }
                            setResult("success");
                          } else {
                            // fail
                            if (result === "success") {
                              setScore(score - 2);
                            }
                            setResult("failure");
                          }
                        } else {
                          // progress
                          if (result === "success") {
                            setScore(score - 1);
                          } else if (result === "failure") {
                            //   setScore(score + 1);
                          }
                          setResult("progress");
                        }
                      }
                    }}
                  ></input>
                );
              })}
            </section>
          );
        })}
      </form>
    );
  }
);

const WordElement = React.forwardRef(
  (
    {
      word,
      shuffled,
      scoreUtils,
      showSolution,
    }: {
      word: String;
      shuffled: String;
      scoreUtils: [number, React.Dispatch<React.SetStateAction<number>>];
      showSolution: boolean;
    },
    ref: ForwardedRef<any>
  ) => {
    const [result, setResult] = useState("progress");
    const colors: Record<string, string> = {
      progress: "lightblue",
      success: "lightgreen",
      failure: "red",
    };
    return (
      <div
        tw="text-center text-xl lg:text-2xl font-display p-8 w-full not-last-of-type:mb-1 md:m-1 max-w-3xl flex-auto flex flex-col items-center justify-evenly"
        css={css`
          background-color: ${colors[result]};
          @media (min-width: 768px) {
            width: 47.5%;
          }
        `}
        onClick={(e: React.MouseEvent) => {
          let target = e.target as HTMLElement;

          // don't do anything if they clicked directly on a letter input
          if (target.nodeName === "INPUT") {
            return;
          }

          // rewind to the parent WordElement
          while (target.nodeName !== "DIV") {
            target = target.parentElement ?? target;
          }

          // parse DOM to get first character input field
          const firstChar = target.childNodes[1].childNodes[0]
            .childNodes[0] as HTMLInputElement;
          firstChar.focus();
        }}
      >
        <h1 tw="font-bold text-4xl md:text-3xl lg:text-5xl tracking-wider mb-2">
          {shuffled}
        </h1>
        {showSolution && <h3>{word}</h3>}
        <WordForm
          word={word}
          ref={ref}
          resultUtils={[result, setResult]}
          scoreUtils={scoreUtils}
        />
        {/* <br />
        {word} */}
      </div>
    );
  }
);

const Scramble = ({ data }: { data: Datafile }) => {
  const page = data.dataJson;
  const [score, setScore] = useState(0);
  const [numIterations, setNumIterations] = useState(0);

  const brainImg = getImage(
    data.allImageSharp.edges.filter((e: any) =>
      e.node.fluid.originalName.includes(page.slug)
    )[0].node
  );
  //   console.log(brainImg);

  const currLetter = useRef<HTMLFormElement>(null);

  const x = useEffect(() => {
    document.addEventListener("keyup", () => {
      if (currLetter.current) {
        // console.log(currLetter.current);
        currLetter.current?.select();
      }
    });
  }, [currLetter]);

  const generateWords = () => {
    const words = _.shuffle(page.wordlist).slice(0, 6);
    const shuffledWords = words.map((e) => {
      return e
        .split(" ")
        .map((e: String) => {
          return _.shuffle(e).join("");
        })
        .join(" ");
    });
    return words.map((e, i) => {
      let o = { word: e, shuffled: shuffledWords[i] };
      return o;
    });
  };

  const prelimWords = generateWords();
  const [words, setWords] = useState(prelimWords);

  // resets score, generates new words
  const resetGame = () => {
    setScore(0);
    setNumIterations(numIterations + 1);
    setWords(generateWords());
  };

  const [showSolution, setShowSolution] = useState(false);

  return (
    <Layout>
      <div tw="flex flex-col-reverse md:flex-row place-content-stretch md:h-screen w-screen">
        <div tw="md:flex-grow flex flex-col h-full p-4">
          <div
            tw="h-24 md:h-1/6 mb-4 py-4 md:py-0 flex flex-row items-center justify-around md:justify-between font-display text-5xl overflow-x-hidden"
            css={css`
              background-color: #38383a;
            `}
          >
            <img
              src={HeaderImg}
              tw="hidden md:inline object-scale-down max-h-full w-1/4 lg:w-auto"
              alt="a thought bubble with the words Brain Word Scramble and a brain glyph"
            />

            <div tw="text-white text-center text-2xl leading-none md:leading-normal xl:text-6xl font-bold  tracking-wide">
              {page.title.split(" ").length > 3 ? (
                <div tw="text-lg md:text-2xl">
                  How Marijuana Affects
                  <div tw="text-base md:text-xl lg:text-4xl">
                    the Developing Brain
                  </div>
                </div>
              ) : (
                page.title
              )}
            </div>
            {brainImg && (
              <GatsbyImage
                tw="h-full w-24 md:w-auto max-w-sm md:max-w-md xl:max-w-xl md:-mr-16"
                //   aspectRatio={1.635789474}
                //   layout="constrained"
                imgStyle={{ objectFit: "contain" }}
                //   placeholder="blurred"
                image={brainImg}
                alt={page.title.toString()}
              />
            )}
          </div>
          <div tw="border-2 h-5/6 mx-auto flex flex-row flex-wrap items-center items-stretch justify-around bg-white bg-opacity-50 flex-shrink w-full p-1 md:overflow-y-scroll">
            {words.map(
              ({ word, shuffled }: { word: String; shuffled: String }, _i) => (
                <WordElement
                  word={word}
                  shuffled={shuffled}
                  key={_i + numIterations * words.length}
                  ref={currLetter}
                  scoreUtils={[score, setScore]}
                  showSolution={showSolution}
                />
              )
            )}
          </div>
        </div>
        <Sidebar
          css={css`
            background-image: url(http://asklistenlearn.org/play/Brain_Trivia/jep-bg.b1a7bded.png);
            background-size: cover;
            background-position: center center;
            font-family: Lunchbox;
            color: white;
          `}
          tw="w-screen md:w-80 text-center text-4xl sticky top-0 md:relative items-center justify-center flex-wrap md:flex-nowrap pb-4"
        >
          <div
            tw="hidden md:flex h-24 absolute top-0 left-0 p-4 items-center justify-center"
            css={css`
              background-color: #c8df54;
            `}
          >
            <img src={AskLogo} alt="Ask Listen Learn logo" />
          </div>
          <StaticImage
            src="../img/sidebar.png"
            alt={page.title.toString()}
            tw="m-4 hidden md:inline md:m-0 w-24 md:w-full"
            placeholder="blurred"
          />
          <StaticImage
            src="../img/sidebar-mobile.png"
            alt={page.title.toString()}
            tw="md:hidden w-2/5"
            placeholder="blurred"
          />
          <div tw="border-white border-2 border-dashed md:w-4/5 my-4 md:my-8 py-2 px-2 md:px-0 md:py-8">
            <div tw="text-5xl">{score} / 6</div>
            <div tw="text-2xl">words unscrambled</div>
          </div>
          <div tw="flex flex-col">
            <PrimaryButton tw="mb-2" onClick={resetGame}>
              New Scramble
            </PrimaryButton>
            <PrimaryButton
              tw="mb-2"
              onClick={() => setShowSolution(!showSolution)}
            >
              {showSolution ? "Hide" : "Show"} Solution
            </PrimaryButton>
            <a href={vocab[page.slug]} target="_blank">
              <PrimaryButton>Show Vocab</PrimaryButton>
            </a>
          </div>
        </Sidebar>
      </div>
    </Layout>
  );
};

export const query = graphql`
  query($slug: String!) {
    dataJson(slug: { eq: $slug }) {
      slug
      title
      wordlist
      wordsearch {
        rows
        cols
        grid
        solution
      }
    }
    allImageSharp {
      edges {
        node {
          id
          gatsbyImageData
          fluid {
            originalName
            src
          }
        }
      }
    }
  }
`;

export default Scramble;
